import localeManifest from "@/i18n/locales/manifest.json";

export type LocaleTag = "en" | "en-US" | "en-GB" | "fr" | "de" | "es";
export type RuntimeShellLanguage = "en" | "en-US" | "en-GB";
export type RuntimeShellDirection = "ltr" | "rtl";
export type LocaleActivationStatus =
  | "source"
  | "regional-english-source-reviewed"
  | "translation-review-required"
  | "translation-reviewed";

export type LocaleRegistryEntry = {
  englishName: string;
  nativeName: string;
  labelKey: string;
  direction: RuntimeShellDirection;
  catalog: string | null;
  fallbackLocale: LocaleTag | null;
  activationStatus: LocaleActivationStatus;
  runtimeSelectable: boolean;
  translationReviewRequired: boolean;
};

type LocaleManifest = {
  version: number;
  sourceLocale: LocaleTag;
  defaultRuntimeLocale: RuntimeShellLanguage;
  firstProductionLocales: LocaleTag[];
  locales: Record<LocaleTag, LocaleRegistryEntry>;
};

const manifest = localeManifest as LocaleManifest;

export const SOURCE_LOCALE = manifest.sourceLocale;
export const DEFAULT_RUNTIME_SHELL_LANGUAGE = manifest.defaultRuntimeLocale;
export const FIRST_PRODUCTION_LANGUAGE_TARGETS = manifest.firstProductionLocales;
export const LOCALE_REGISTRY = manifest.locales;
export const RUNTIME_SHELL_LANGUAGES = Object.entries(LOCALE_REGISTRY)
  .filter(([, locale]) => locale.runtimeSelectable)
  .map(([locale]) => locale) as RuntimeShellLanguage[];
export const RUNTIME_SHELL_DIRECTIONS = ["ltr", "rtl"] as const;

const runtimeLanguageSet = new Set<string>(RUNTIME_SHELL_LANGUAGES);
const localeAliases = new Map<string, LocaleTag>([
  ["en-us", "en-US"],
  ["en-gb", "en-GB"],
  ["en-uk", "en-GB"],
  ["fr-fr", "fr"],
  ["de-de", "de"],
  ["es-es", "es"]
]);

export function getRuntimeShellDirection(language: RuntimeShellLanguage): RuntimeShellDirection {
  return LOCALE_REGISTRY[language].direction;
}

export function isRuntimeShellLanguage(value: string): value is RuntimeShellLanguage {
  return runtimeLanguageSet.has(value);
}

export function normalizeLocaleTag(value: string | null | undefined): LocaleTag | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  if (Object.hasOwn(LOCALE_REGISTRY, trimmed)) return trimmed as LocaleTag;

  const canonical = trimmed.toLowerCase();
  return localeAliases.get(canonical) ?? null;
}

export function normalizeRuntimeShellLanguage(
  value: string | null | undefined
): RuntimeShellLanguage {
  const locale = normalizeLocaleTag(value);
  if (locale && isRuntimeShellLanguage(locale)) return locale;
  return DEFAULT_RUNTIME_SHELL_LANGUAGE;
}
